import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { TrendingDown, LineChart, Activity } from 'lucide-react';
interface SlideAIOpportunityProps {
  onContinue: () => void;
}
export function SlideAIOpportunity({
  onContinue
}: SlideAIOpportunityProps) {
  const [stage, setStage] = useState(0);
  const levers = [{
    id: 'erosion',
    title: 'Reduce Erosion',
    desc: 'Continuous, data-driven engagement keeps HCPs connected to the brand — actively defending market share instead of letting it fade.',
    icon: <TrendingDown className="w-6 h-6 text-teal-400" data-id="element-148" />,
    color: 'from-teal-400/20 to-teal-400/5',
    borderColor: 'border-teal-400/30'
  }, {
    id: 'profitability',
    title: 'Increase Profitability',
    desc: 'Replacing traditional marketing with AI-automated execution widens the margin on every euro of legacy revenue.',
    icon: <LineChart className="w-6 h-6 text-purple-400" data-id="element-149" />,
    color: 'from-purple-500/20 to-purple-500/5',
    borderColor: 'border-purple-500/30'
  }, {
    id: 'lifespan',
    title: 'Prolong Lifespan',
    desc: 'Lower costs and slower erosion keep the gap between revenue and cost positive for longer, keeping products on market.',
    icon: <Activity className="w-6 h-6 text-rose-400" data-id="element-150" />,
    color: 'from-rose-400/20 to-rose-400/5',
    borderColor: 'border-rose-400/30'
  }];
  useEffect(() => {
    const timers = levers.map((_, idx) => setTimeout(() => setStage(idx), 600 + idx * 800));
    return () => timers.forEach(clearTimeout);
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-14 max-w-7xl mx-auto" data-id="element-151">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="text-center space-y-4" data-id="element-152">
        <h2 className="text-4xl md:text-5xl font-serif text-white" data-id="element-153">
          Three Levers
        </h2>
        <p className="text-xl text-white/60" data-id="element-154">
          How we compound value across the legacy portfolio.
        </p>
      </motion.div>

      <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-6 items-center max-w-6xl mx-auto w-full mt-10" data-id="element-155">
        {levers.map((lever, idx) => <motion.div key={lever.id} initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: stage >= idx ? 1 : 0,
        y: stage >= idx ? 0 : 30
      }} transition={{
        duration: 0.5
      }} className={`bg-gradient-to-br ${lever.color} border ${lever.borderColor} p-8 rounded-2xl h-full flex flex-col backdrop-blur-sm`} data-id="element-156">
            <div className="w-12 h-12 rounded-xl bg-navy-800/60 border border-navy-600 flex items-center justify-center mb-6" data-id="element-157">
              {lever.icon}
            </div>
            <h3 className="text-xl font-semibold text-white mb-3" data-id="element-158">
              {lever.title}
            </h3>
            <p className="text-white/65 leading-relaxed flex-1" data-id="element-159">{lever.desc}</p>
          </motion.div>)}
      </div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: stage >= 4 ? 1 : 0
    }} className="mt-12 flex justify-center" data-id="element-160">
        <Button onClick={onContinue} data-id="element-161">Operating Model</Button>
      </motion.div>
    </div>;
}